/**
 * Enabled nav-modes configuration (issue #143): the single source of truth for
 * which navigation-mode buttons the toolbar renders and which mode the viewer
 * boots in. The community-facing default exposes just Explore + Drive (Route,
 * relabelled); Orbit / Fly / Walk are retained in code but hidden from the UI
 * unless a config re-enables them.
 *
 * Pure and dependency-free (only the `NavMode` string-union type from
 * cameraRig), so it is unit-tested in plain Node exactly like
 * core/shadingPreset.ts and ui/layoutMode.ts. `main.ts` reads
 * `window.location.search`, renders the resolved buttons into `#mode-control`,
 * and seeds the rig + controls legend from the resolved boot mode; nothing
 * here touches the DOM, three.js, or any LOS result (hard constraint #1).
 *
 * The URL override follows the repo's existing `parse*Override(search)`
 * convention (core/quality.ts, core/shadingPreset.ts, scene/colorGrade.ts):
 * `?modes=explore,orbit,fly,walk,route` names an explicit ordered set, and the
 * `?modes=all` sentinel enables every mode. A missing param, an empty set, or
 * an all-unknown set all fall back to the documented default — the toolbar is
 * never modeless (hard constraint #7).
 */
import type { NavMode } from '../camera/cameraRig';

/**
 * Every mode the code knows, in the canonical order used to expand the
 * `?modes=all` sentinel. Explore leads so it is the default-active (and boot)
 * mode whenever it is enabled — matching the default set below.
 */
export const ALL_NAV_MODES: readonly NavMode[] = ['explore', 'orbit', 'fly', 'walk', 'route'];

/**
 * The community-facing default enabled set: Explore + Drive (Route). The array
 * order is the toolbar render order; the first entry is the default boot /
 * active mode. A future committed-config source replaces this one constant —
 * everything else derives from it.
 */
export const DEFAULT_ENABLED_NAV_MODES: readonly NavMode[] = ['explore', 'route'];

/**
 * User-facing button labels. Every id shows its own name except `route`, which
 * reads "Drive" (issue #143) while the internal mode id stays `'route'` — a
 * label-only relabel, so `data-mode="route"`, RoutePlayer and the route strip
 * are all unchanged.
 */
export const NAV_MODE_LABELS: Record<NavMode, string> = {
  orbit: 'Orbit',
  explore: 'Explore',
  fly: 'Fly',
  walk: 'Walk',
  route: 'Drive',
};

function isNavMode(value: string): value is NavMode {
  return (ALL_NAV_MODES as readonly string[]).includes(value);
}

/**
 * Reads `?modes=` from a location search string. Returns:
 *  - `undefined` when the param is absent, empty, or names only unknown modes
 *    (the caller then uses {@link DEFAULT_ENABLED_NAV_MODES});
 *  - the canonical all-modes list for the `all` sentinel;
 *  - otherwise the recognised modes in the order given, de-duplicated, with
 *    unknown tokens dropped (predictable failure case: an unknown mode renders
 *    no button rather than breaking the parse).
 *
 * Never throws — an unparseable value degrades to the default, never a crash.
 */
export function parseNavModesOverride(search: string): NavMode[] | undefined {
  const raw = new URLSearchParams(search).get('modes');
  if (raw === null) return undefined;
  const trimmed = raw.trim().toLowerCase();
  if (trimmed === 'all') return [...ALL_NAV_MODES];
  const result: NavMode[] = [];
  for (const token of trimmed.split(',')) {
    const mode = token.trim();
    if (isNavMode(mode) && !result.includes(mode)) result.push(mode);
  }
  return result.length > 0 ? result : undefined;
}

/**
 * The resolved enabled set: the URL override when it yields at least one valid
 * mode, else the documented default. Always non-empty, so the toolbar always
 * renders at least the default buttons (hard constraint #7 — an empty or
 * all-invalid config degrades to the default, never a modeless UI).
 */
export function resolveEnabledNavModes(search: string): NavMode[] {
  return parseNavModesOverride(search) ?? [...DEFAULT_ENABLED_NAV_MODES];
}

/**
 * The boot / default-active mode for an enabled set: its first entry. Falls
 * back to the first default mode if somehow handed an empty list, so the rig
 * always boots in a mode whose button is actually rendered (never a hidden
 * mode — predictable failure case).
 */
export function defaultNavMode(enabled: readonly NavMode[]): NavMode {
  return enabled[0] ?? DEFAULT_ENABLED_NAV_MODES[0];
}

/** A rendered toolbar button descriptor. `active` marks the default (first)
 * mode of the enabled set. */
export interface NavModeButtonSpec {
  mode: NavMode;
  label: string;
  active: boolean;
}

/**
 * Button specs for an enabled set, in render order, with the first (default)
 * mode marked active. The pure decision behind which buttons the toolbar
 * renders and which starts active — unit-tested without a DOM.
 */
export function navModeButtonSpecs(enabled: readonly NavMode[]): NavModeButtonSpec[] {
  return enabled.map((mode, index) => ({ mode, label: NAV_MODE_LABELS[mode], active: index === 0 }));
}

/**
 * The `#mode-control` inner HTML for an enabled set — the hardcoded 5-button
 * template's replacement (issue #143). Labels are fixed internal constants
 * (never user input), so plain interpolation is safe; `data-mode` carries the
 * internal id so the existing `[data-mode]`→`setMode` handler is untouched.
 */
export function navModeButtonsHtml(enabled: readonly NavMode[]): string {
  return navModeButtonSpecs(enabled)
    .map(
      (button) =>
        `<button data-mode="${button.mode}" type="button"${button.active ? ' class="active"' : ''}>${button.label}</button>`,
    )
    .join('');
}
